/**
 * useBgJobs · context globale per job in background "client-side" (foto
 * enhance/restyle/empty-room/stage, video 8s prima/dopo, caption social,
 * ecc.) che NON hanno polling server-side (a differenza dei pack social
 * gestiti da usePackJobs).
 *
 * Differenze rispetto a usePackJobs:
 *   - I job vivono in memoria (NIENTE localStorage). Sopravvivono ai cambi
 *     di pagina dentro la SPA, ma non al refresh: questo riflette la
 *     realtà tecnica (le fetch client-side muoiono al reload).
 *   - Nessun polling backend. Chi lancia il job chiama track() e quando
 *     la promise si chiude (success o error) chiama end().
 *   - Auto-cleanup dopo 8s dal completamento di un job "done" (lascia il
 *     tempo all'utente di vedere il check, poi sparisce dal tray).
 *
 * Pubblicato via context. Provider montato in App.js (sopra PackJobsTray).
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

const BgJobsContext = createContext(null);

// Etichette user-facing per ogni tipo di job. L'agente non legge "photo_enhance"
// ma "Migliora foto" nel tray. Restano sincronizzate con ACTION_LABELS in
// pages/Crediti.jsx (lì sono per il ledger storico, qui per il live tracking).
export const BG_JOB_LABELS = {
  photo_enhance:    "Migliora foto",
  photo_restyle:    "Rinnova arredo",
  photo_empty_room: "Svuota stanza",
  photo_stage:      "Arreda spazio",
  floor_plan:       "Arreda piantina",
  video_before_after: "Video Prima/Dopo",
  caption_social:   "Caption social",
  social_asset:     "Asset social",
  reel_render:      "Reel in lavorazione",
};

// Dopo che un job va in stato done/failed, lo togliamo dal tray dopo
// auto-dismiss così l'agente vede il check verde un secondo prima che sparisca.
const AUTO_DISMISS_DONE_MS = 6000;
const AUTO_DISMISS_FAILED_MS = 12000;

export function BgJobsProvider({ children }) {
  const [jobs, setJobs] = useState([]);
  const idCounterRef = useRef(0);

  // Auto-dismiss: ogni volta che la lista cambia, programmo timer di rimozione
  // per i job che sono done o failed.
  useEffect(() => {
    const timers = jobs
      .filter((j) => j.status === "done" || j.status === "failed")
      .map((j) => {
        const ms = j.status === "done" ? AUTO_DISMISS_DONE_MS : AUTO_DISMISS_FAILED_MS;
        const due = (j.ended_at || Date.now()) + ms - Date.now();
        // eslint-disable-next-line react-hooks/set-state-in-effect
        const t = setTimeout(() => {
          setJobs((prev) => prev.filter((x) => x.id !== j.id));
        }, Math.max(0, due));
        return t;
      });
    return () => {
      timers.forEach((t) => clearTimeout(t));
    };
  }, [jobs]);

  // start(kind, label?) → restituisce {id, end(success?), fail(msg?)}.
  // Se label non passato, usa BG_JOB_LABELS[kind] o "Generazione".
  const start = useCallback((kind, labelOverride) => {
    idCounterRef.current += 1;
    const id = `bg_${Date.now()}_${idCounterRef.current}`;
    const label = labelOverride || BG_JOB_LABELS[kind] || "Generazione";
    const newJob = {
      id,
      kind,
      label,
      status: "running",
      started_at: Date.now(),
      ended_at: null,
      error: null,
    };
    setJobs((prev) => [...prev, newJob]);
    return {
      id,
      end: (success = true, error = null) => {
        setJobs((prev) =>
          prev.map((j) =>
            j.id === id
              ? {
                  ...j,
                  status: success ? "done" : "failed",
                  ended_at: Date.now(),
                  error: error || null,
                }
              : j,
          ),
        );
      },
      fail: (msg) => {
        setJobs((prev) =>
          prev.map((j) =>
            j.id === id
              ? { ...j, status: "failed", ended_at: Date.now(), error: msg || null }
              : j,
          ),
        );
      },
    };
  }, []);

  const remove = useCallback((id) => {
    setJobs((prev) => prev.filter((j) => j.id !== id));
  }, []);

  const value = useMemo(
    () => ({ jobs, start, remove }),
    [jobs, start, remove],
  );

  return <BgJobsContext.Provider value={value}>{children}</BgJobsContext.Provider>;
}

export function useBgJobs() {
  const ctx = useContext(BgJobsContext);
  if (!ctx) {
    throw new Error("useBgJobs deve essere usato dentro <BgJobsProvider>");
  }
  return ctx;
}

/**
 * Helper che wrappa una promise registrando un job nel tray globale.
 *
 *   const data = await runBgJob(start, "photo_enhance", () =>
 *     api.post("/live/enhance", body)
 *   );
 *
 * Comodo per call-site singoli. Per batch (es. enhance multiple foto in
 * parallelo) chiama start() / end() esplicitamente per ogni item.
 */
export async function runBgJob(startFn, kind, fn, labelOverride) {
  const handle = startFn(kind, labelOverride);
  try {
    const result = await fn();
    handle.end(true);
    return result;
  } catch (err) {
    handle.end(false, err?.message || String(err));
    throw err;
  }
}
